import React, { useEffect, useState } from 'react';
import onboardingService from '../services/OnboardingService';
import { OnboardingInvitePreview } from '../types/Onboarding';
import { useTheme } from '../contexts/ThemeContext';

interface AcceptInvitationPageProps {
  token: string;
}

const AcceptInvitationPage: React.FC<AcceptInvitationPageProps> = ({ token }) => {
  const { colors } = useTheme();
  const [preview, setPreview] = useState<OnboardingInvitePreview | null>(null);
  const [name, setName] = useState('');
  const [organization, setOrganization] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await onboardingService.getInvitePreview(token);
        if (cancelled) return;
        setPreview(result);
        setName(result.name || '');
        setOrganization(result.organization || '');
      } catch (err: any) {
        if (!cancelled) {
          setError(err.message || 'Failed to load invitation');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const cardStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '520px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '16px',
    padding: '28px',
    boxShadow: `0 12px 30px ${colors.shadowLight || 'rgba(0, 0, 0, 0.12)'}`
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.textPrimary,
    fontSize: '14px'
  };

  const primaryButtonStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.primary}`,
    background: colors.primary,
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer'
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...primaryButtonStyle,
    background: colors.background,
    color: colors.textPrimary,
    border: `1px solid ${colors.border}`
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!preview) return;
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await onboardingService.acceptInvite(token, {
        name: name.trim(),
        organization: organization.trim(),
        password
      });
      setAccepted(true);
    } catch (err: any) {
      setError(err.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      background: colors.background
    }}>
      <div style={cardStyle}>
        {loading ? (
          <div style={{ color: colors.textSecondary }}>Loading invitation...</div>
        ) : error ? (
          <div>
            <h1 style={{ marginTop: 0, color: colors.textPrimary }}>Invitation</h1>
            <div style={{
              marginBottom: '16px',
              padding: '12px 14px',
              borderRadius: '10px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#991b1b'
            }}>
              {error}
            </div>
            <button style={secondaryButtonStyle} onClick={() => { window.location.href = '/'; }}>
              Back to login
            </button>
          </div>
        ) : accepted ? (
          <div>
            <h1 style={{ marginTop: 0, color: colors.textPrimary }}>Access activated</h1>
            <p style={{ color: colors.textSecondary, lineHeight: 1.6 }}>
              Your access to <strong style={{ color: colors.textPrimary }}>{preview?.siteName}</strong> is ready.
              You can now sign in with the password you just set.
            </p>
            <button style={primaryButtonStyle} onClick={() => { window.location.href = '/'; }}>
              Go to login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <h1 style={{ marginTop: 0, marginBottom: '10px', color: colors.textPrimary }}>Accept invitation</h1>
            <p style={{ marginTop: 0, marginBottom: '20px', color: colors.textSecondary, lineHeight: 1.6 }}>
              Activate your access to <strong style={{ color: colors.textPrimary }}>{preview?.siteName}</strong>.
            </p>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: colors.textSecondary, fontSize: '13px' }}>
                Email
              </label>
              <input value={preview?.email || ''} disabled style={{ ...inputStyle, opacity: 0.75 }} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: colors.textSecondary, fontSize: '13px' }}>
                Full name
              </label>
              <input value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '14px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: colors.textSecondary, fontSize: '13px' }}>
                Organization
              </label>
              <input value={organization} onChange={(e) => setOrganization(e.target.value)} style={inputStyle} />
            </div>

            <div style={{ marginBottom: '18px' }}>
              <label style={{ display: 'block', marginBottom: '6px', color: colors.textSecondary, fontSize: '13px' }}>
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={inputStyle}
                placeholder="Minimum 8 characters"
              />
            </div>

            {preview?.expiresAt && (
              <div style={{ marginBottom: '18px', color: colors.textSecondary, fontSize: '12px' }}>
                This invitation expires on {new Date(preview.expiresAt).toLocaleString()}.
              </div>
            )}

            <button type="submit" style={primaryButtonStyle} disabled={submitting}>
              {submitting ? 'Activating...' : 'Activate access'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AcceptInvitationPage;
